/**
 * useSpeech — sintesi vocale via `window.speechSynthesis` (web + desktop).
 * Nessun servizio esterno: la voce è locale al dispositivo (§ Security &
 * Privacy del task). Sceglie la prima voce `it-IT`/`en-US` disponibile,
 * altrimenti lascia decidere al motore.
 */
import { useCallback, useMemo } from 'react'
import type { MapLocale } from '@arianna/reader-core'

const LANG_TAG: Record<MapLocale, string> = { it: 'it-IT', en: 'en-US' }

export interface UseSpeechResult {
  speak(text: string): void
  stop(): void
  supported: boolean
}

export function useSpeech(locale: MapLocale): UseSpeechResult {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window

  const speak = useCallback(
    (text: string) => {
      if (!supported || text.trim().length === 0) return
      const synth = window.speechSynthesis
      synth.cancel()
      const utterance = new SpeechSynthesisUtterance(text)
      const lang = LANG_TAG[locale]
      utterance.lang = lang
      const voice = synth.getVoices().find((v) => v.lang === lang)
      if (voice != null) utterance.voice = voice
      synth.speak(utterance)
    },
    [locale, supported],
  )

  const stop = useCallback(() => {
    if (supported) window.speechSynthesis.cancel()
  }, [supported])

  return useMemo(() => ({ speak, stop, supported }), [speak, stop, supported])
}
